/**
 * Centralized API Service Module
 * Handles all backend API calls with consistent error handling and authentication
 * Updated for new backend API structure
 */

import logger from '../utils/logger';

// Base URL for API calls - configurable via environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get authentication headers for API requests
 * @param {string} accessToken - The user's access token
 * @returns {Object} Headers object with Authorization and Content-Type
 */
const getAuthHeaders = (accessToken) => {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${accessToken}`
  };
};

/**
 * Handle API response with standardized processing
 * @param {Response} response - Fetch API response object
 * @returns {Promise<Object>} Parsed response data
 */
const handleApiResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  
  if (!response.ok) {
    // Handle error response format: { success: false, error: "message", request_id: "..." }
    const error = new Error(data.detail || data.error || data.message || `HTTP error! status: ${response.status}`);
    error.status = response.status;
    error.response = data;
    throw error;
  }
  
  // Handle success response format: { success: true, message: "...", data: {...} }
  return data;
};

/**
 * Handle API errors with standardized error handling
 * Enhancement: Provides user-friendly error messages instead of technical jargon
 * @param {Error} error - Error object from API call
 * @returns {Object} Standardized error object
 */
const handleApiError = (error) => {
  logger.error('API Error', { error: error.message, stack: error.stack });
  
  // Fix: Context-aware error messages for better user experience
  // Maps technical errors to actionable user guidance
  let userMessage;
  
  // Check for database-specific error messages from backend
  if (error.status === 503 && error.message) {
    // Database timeout/unavailable errors from backend
    if (error.message.includes('slow to respond') || error.message.includes('temporarily unavailable')) {
      userMessage = error.message; // Use the backend's user-friendly message
    } else {
      userMessage = 'Database was on standby and is currently slow to respond. Please try again in 60 seconds.';
    }
  } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
    // Most common case: backend server not running
    userMessage = 'Unable to connect to the server. Please ensure the backend service is running and try again.';
  } else if (error.message.includes('NetworkError') || error.message.includes('fetch')) {
    // Network connectivity issues
    userMessage = 'Network connection error. Please check your internet connection and try again.';
  } else if (error.message.includes('timeout')) {
    // Server performance issues
    userMessage = 'Request timed out. The server may be experiencing high load. Please try again.';
  } else {
    // Fallback for any other errors
    userMessage = error.message || 'An unexpected error occurred. Please try again.';
  }
  
  return {
    success: false,
    message: userMessage,
    error: error.message || 'An unexpected error occurred'
  };
};

/**
 * Login user with username and password
 * @param {string} username - User's username
 * @param {string} password - User's password
 * @returns {Promise<Object>} Login response with access token, refresh token, and user info
 */
export const login = async (username, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    
    const result = await handleApiResponse(response);
    return result;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Refresh access token using refresh token
 * @param {string} refreshToken - User's refresh token
 * @returns {Promise<Object>} New tokens response
 */
export const refreshToken = async (refreshToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Logout user and invalidate refresh token
 * @param {string} refreshToken - User's refresh token
 * @returns {Promise<Object>} Logout response
 */
export const logout = async (refreshToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get current authenticated user information
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} User information response
 */
export const getUserInfo = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/auth/user`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken),
    });

    return await handleApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get the system prompt
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} System prompt response
 */
export const getSystemPrompt = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/subject-prompts/system?agent=GPT`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken),
    });

    const result = await handleApiResponse(response);
    // Normalize API changes: backend now returns { agent, subject, text }
    // Map to existing frontend shape: { agent, subject, prompt }
    if (result && result.data) {
      const d = result.data;
      result.data = {
        agent: d.agent || 'gpt',
        subject: d.subject || 'system',
        prompt: d.text ?? d.prompt ?? ''
      };
    }
    return result;
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Send chat message to the AI assistant
 * @param {string} message - User's message
 * @param {Array} chatHistory - Array of previous conversation messages including system prompt
 * @param {string} accessToken - User's access token
 * @param {string} subject - Optional subject/topic
 * @param {string} agent - Optional agent (default "GPT")
 * @param {boolean} agentsUsed - Whether MCP agents are used by subject prompt
 * @param {string} conversationId - Optional conversation ID
 * @param {number} temperature - Optional temperature (0.0-2.0, default 0.7)
 * @param {number} maxTokens - Optional max tokens (1-4000, default 1000)
 * @returns {Promise<Object>} AI response data
 */
export const sendChatMessage = async (message, chatHistory, accessToken, subject = null, agent = 'GPT', agentsUsed = false, conversationId = null, temperature = 0.7, maxTokens = 1000) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/chat/current/send`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        message,
        chat_history: chatHistory,
        subject,
        agent,
        agents_used: agentsUsed,
        conversation_id: conversationId,
        temperature,
        max_tokens: maxTokens,
        user_fact_detection_check: false
      })
    });

    return await handleApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Clear chat conversation (placeholder for future LLM support)
 * @param {string} conversationId - Conversation identifier
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Clear operation response
 */
export const clearChat = async (conversationId, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/chat/current/conversation`, {
      method: 'DELETE',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        conversation_id: conversationId
      })
    });

    return await handleApiResponse(response);
  } catch (error) {
    return handleApiError(error);
  }
};

// ===== SAVED CHAT FUNCTIONS =====

/**
 * Get list of saved chats for the current user
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} List of saved chat summaries
 */
export const getSavedChats = async (accessToken) => {
  try {
    logger.info('Fetching saved chats list');
    
    const response = await fetch(`${API_BASE_URL}/v1/chats/saved`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Saved chats list fetched successfully', { count: result.data?.length || 0 });
    return result;
  } catch (error) {
    logger.error('Failed to fetch saved chats list', { error: error.message });
    return handleApiError(error);
  }
};

/**
 * Get a specific saved chat by ID
 * @param {string} chatId - MongoDB ObjectId of the chat
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Saved chat with full message history
 */
export const getSavedChat = async (chatId, accessToken) => {
  try {
    logger.info('Fetching saved chat', { chatId });
    
    const response = await fetch(`${API_BASE_URL}/v1/chats/saved/${chatId}`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Saved chat fetched successfully', { chatId, chatName: result.data?.chat_name });
    return result;
  } catch (error) {
    logger.error('Failed to fetch saved chat', { chatId, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Save a new chat
 * @param {string} chatName - User-provided chat title
 * @param {Array} messages - Array of chat messages to save
 * @param {string} accessToken - User's access token
 * @param {string|null} subject - Optional subject field (alphanumeric, 1-200 chars)
 * @returns {Promise<Object>} Save operation response with chat ID
 */
export const saveChat = async (chatName, messages, accessToken, subject = null) => {
  try {
    // Validate subject field
    if (subject !== null && subject !== undefined) {
      if (typeof subject !== 'string' || subject.length === 0) {
        throw new Error('Subject must be a non-empty string');
      }
      if (subject.length > 200) {
        throw new Error('Subject cannot exceed 200 characters');
      }
      if (!/^[a-zA-Z0-9\s\-.]+$/.test(subject)) {
        throw new Error('Subject can only contain alphanumeric characters, spaces, hyphens, and periods');
      }
    }
    
    logger.info('Saving new chat', { chatName, messageCount: messages.length, subject });
    
    const response = await fetch(`${API_BASE_URL}/v1/chats/saved`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        chat_name: chatName,
        messages: messages,
        subject: subject
      })
    });

    const result = await handleApiResponse(response);
    logger.info('Chat saved successfully', { chatId: result.data?.chat_id, chatName, subject });
    return result;
  } catch (error) {
    logger.error('Failed to save chat', { chatName, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Update an existing saved chat
 * @param {string} chatId - MongoDB ObjectId of the chat
 * @param {string} chatName - Updated chat title
 * @param {Array} messages - Updated array of chat messages
 * @param {string} accessToken - User's access token
 * @param {string|null} subject - Optional subject field (alphanumeric, 1-200 chars)
 * @returns {Promise<Object>} Update operation response
 */
export const updateSavedChat = async (chatId, chatName, messages, accessToken, subject = null) => {
  try {
    // Validate subject field
    if (subject !== null && subject !== undefined) {
      if (typeof subject !== 'string' || subject.length === 0) {
        throw new Error('Subject must be a non-empty string');
      }
      if (subject.length > 200) {
        throw new Error('Subject cannot exceed 200 characters');
      }
      if (!/^[a-zA-Z0-9\s\-.]+$/.test(subject)) {
        throw new Error('Subject can only contain alphanumeric characters, spaces, hyphens, and periods');
      }
    }
    
    logger.info('Updating saved chat', { chatId, chatName, messageCount: messages.length, subject });
    
    const response = await fetch(`${API_BASE_URL}/v1/chats/saved/${chatId}`, {
      method: 'PUT',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        chat_name: chatName,
        messages: messages,
        subject: subject
      })
    });

    const result = await handleApiResponse(response);
    logger.info('Chat updated successfully', { chatId, chatName, subject });
    return result;
  } catch (error) {
    logger.error('Failed to update chat', { chatId, chatName, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Delete a saved chat
 * @param {string} chatId - MongoDB ObjectId of the chat
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Delete operation response
 */
export const deleteSavedChat = async (chatId, accessToken) => {
  try {
    logger.info('Deleting saved chat', { chatId });
    
    const response = await fetch(`${API_BASE_URL}/v1/chats/saved/${chatId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Chat deleted successfully', { chatId });
    return result;
  } catch (error) {
    logger.error('Failed to delete chat', { chatId, error: error.message });
    return handleApiError(error);
  }
};

// ===== PROFILE FUNCTIONS =====

/**
 * Get profile facts for the current user
 * @param {string} accessToken - User's access token
 * @param {string} factType - Optional fact type filter
 * @returns {Promise<Object>} List of profile facts
 */
export const getProfileFacts = async (accessToken, factType = null) => {
  try {
    logger.info('Fetching profile facts', { factType });
    
    const url = factType 
      ? `${API_BASE_URL}/v1/profile/facts?type=${factType}`
      : `${API_BASE_URL}/v1/profile/facts`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Profile facts fetched successfully', { count: result.facts?.length || 0 });
    return result;
  } catch (error) {
    logger.error('Failed to fetch profile facts', { factType, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Get profile facts organized by type
 * @param {string} accessToken - User's access token
 * @param {Array} types - Array of fact types to retrieve
 * @returns {Promise<Object>} Facts organized by type
 */
export const getProfileFactsByTypes = async (accessToken, types) => {
  try {
    logger.info('Fetching profile facts by types', { types });
    
    const typesParam = types.join(',');
    const response = await fetch(`${API_BASE_URL}/v1/profile/facts-by-types?types=${typesParam}`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Profile facts by types fetched successfully', { types });
    return result;
  } catch (error) {
    logger.error('Failed to fetch profile facts by types', { types, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Create a new profile fact
 * @param {string} factType - Type of fact (personal_info, firstname, lastname, etc.)
 * @param {string} fact - Fact content
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Created fact response
 */
export const createProfileFact = async (factType, fact, accessToken) => {
  try {
    logger.info('Creating profile fact', { factType });
    
    const response = await fetch(`${API_BASE_URL}/v1/profile/facts`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        type: factType,
        fact: fact
      })
    });

    const result = await handleApiResponse(response);
    logger.info('Profile fact created successfully', { factId: result.id, factType });
    return result;
  } catch (error) {
    logger.error('Failed to create profile fact', { factType, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Update an existing profile fact
 * @param {string} factId - ID of the fact to update
 * @param {string} fact - Updated fact content
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Updated fact response
 */
export const updateProfileFact = async (factId, fact, accessToken) => {
  try {
    logger.info('Updating profile fact', { factId });
    
    const response = await fetch(`${API_BASE_URL}/v1/profile/facts/${factId}`, {
      method: 'PUT',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        fact: fact
      })
    });

    const result = await handleApiResponse(response);
    logger.info('Profile fact updated successfully', { factId });
    return result;
  } catch (error) {
    logger.error('Failed to update profile fact', { factId, error: error.message });
    return handleApiError(error);
  }
};

/**
 * Delete a profile fact
 * @param {string} factId - ID of the fact to delete
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Delete operation response
 */
export const deleteProfileFact = async (factId, accessToken) => {
  try {
    logger.info('Deleting profile fact', { factId });
    
    const response = await fetch(`${API_BASE_URL}/v1/profile/facts/${factId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Profile fact deleted successfully', { factId });
    return result;
  } catch (error) {
    logger.error('Failed to delete profile fact', { factId, error: error.message });
    return handleApiError(error);
  }
};

// ===== SESSION STATS =====
/**
 * Post a session stat event
 * @param {{ session_id: string, event: 'login'|'chat_messages'|'logout', count: number }} payload
 * @param {string} accessToken
 * @returns {Promise<Object>} Response data or standardized error
 */
export const postSessionStat = async (payload, accessToken) => {
  try {
    // Keep logging development-only and fail softly
    logger.debug('Posting session stat', { event: payload?.event, count: payload?.count });
    const response = await fetch(`${API_BASE_URL}/v1/session-stats`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify(payload)
    });

    return await handleApiResponse(response);
  } catch (error) {
    // Swallow errors at call sites; return standardized error for optional inspection
    return handleApiError(error);
  }
};

/**
 * Get profile summary
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Profile summary response
 */
export const getProfileSummary = async (accessToken) => {
  try {
    logger.info('Fetching profile summary');
    
    const response = await fetch(`${API_BASE_URL}/v1/profile/summary`, {
      method: 'GET',
      headers: getAuthHeaders(accessToken)
    });

    const result = await handleApiResponse(response);
    logger.info('Profile summary fetched successfully');
    return result;
  } catch (error) {
    logger.error('Failed to fetch profile summary', { error: error.message });
    return handleApiError(error);
  }
};

// ===== USER FACT DETECTION =====

/**
 * Detect personal facts from user messages using external agent integration
 * @param {string} userId - User ID (1-100 characters)
 * @param {string} userMessage - User message (1-4000 characters)
 * @param {string[]} knownFacts - Optional array of known facts (max 100 items, 1000 chars each)
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Detection response
 */
export const detectUserInfo = async (userId, userMessage, knownFacts = [], accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/chat/detect_user_info`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        user_id: userId,
        user_message: userMessage,
        known_facts: knownFacts
      }),
    });

    return await handleApiResponse(response);
  } catch (error) {
    logger.error('User fact detection failed', { 
      userId, 
      messageLength: userMessage.length, 
      knownFactsCount: knownFacts.length,
      error: error.message 
    });
    throw error;
  }
};
// in the /v1/chat/current/send endpoint. No separate frontend call needed.

// ===== SUBJECT CLASSIFICATION =====

/**
 * Classify the subject of a conversation using the external Subject Detection Agent
 * @param {Array} chatHistory - Array of conversation messages
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object>} Subject classification response
 */
export const classifySubject = async (chatHistory, accessToken) => {
  try {
    logger.debug('Classifying conversation subject', { messageCount: chatHistory.length });
    
    const response = await fetch(`${API_BASE_URL}/v1/chat/classify_subject`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        chat_history: chatHistory
      }),
    });

    const result = await handleApiResponse(response);
    logger.info('Subject classification completed', { 
      subject: result.data?.subject, 
      confidence: result.data?.confidence 
    });
    return result;
  } catch (error) {
    logger.error('Subject classification failed', { 
      messageCount: chatHistory.length,
      error: error.message 
    });
    throw error;
  }
};

/**
 * Get subject-specific prompt for a given subject and agent
 * @param {string} subject - The subject/topic for the prompt
 * @param {string} agent - The agent type (e.g., "GPT", "Claude")
 * @param {string} accessToken - User's access token
 * @returns {Promise<Object|null>} Subject prompt details or null if not found
 */
export const getSubjectPrompt = async (subject, agent, accessToken) => {
  try {
    logger.debug('Retrieving subject prompt', { subject, agent });
    
    const response = await fetch(`${API_BASE_URL}/v1/subject-prompts/search`, {
      method: 'POST',
      headers: getAuthHeaders(accessToken),
      body: JSON.stringify({
        agent: agent,
        subject: subject,
        limit: 10
      }),
    });

    const result = await handleApiResponse(response);
    
    if (result.success && result.data && result.data.prompts && result.data.prompts.length > 0) {
      const prompt = result.data.prompts[0];
      const promptText = prompt.text || '';
      const agentsUsed = Boolean(prompt.agents_used);
      
      logger.info('Subject prompt retrieved successfully', { 
        subject, 
        agent,
        promptLength: promptText.length,
        agentsUsed
      });
      
      return { promptText, agentsUsed };
    } else {
      // No prompts found - this is not an error, just log and return null
      logger.warn('No subject prompt found', { subject, agent });
      return null;
    }
  } catch (error) {
    logger.error('Subject prompt retrieval failed', { 
      subject,
      agent,
      error: error.message 
    });
    throw error;
  }
};